import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Image } from 'react-bootstrap';
import { Save, Settings } from 'lucide-react';

interface CompanyConfig {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyTaxId: string;
  companyLogoUrl: string;
  invoiceFooter: string;
}

const API_BASE_URL = 'https://backfacturas.rmtagency.es/api';

const Configuracion: React.FC = () => {
  const [config, setConfig] = useState<CompanyConfig>({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyTaxId: '',
    companyLogoUrl: '',
    invoiceFooter: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/config`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        if (data.companyLogoUrl) {
          setPreviewUrl(data.companyLogoUrl);
        }
      }
    } catch (err) {
      setError('Error al cargar la configuración');
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // First upload logo if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('logo', selectedFile);
        
        const uploadResponse = await fetch(`${API_BASE_URL}/config/logo`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const logoData = await uploadResponse.json();
          setConfig(prev => ({ ...prev, companyLogoUrl: logoData.logoUrl }));
        }
      }
      
      // Then save the configuration
      const response = await fetch(`${API_BASE_URL}/config`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        setSuccess('Configuración guardada correctamente');
        setSelectedFile(null);
      } else {
        throw new Error('Error al guardar la configuración');
      }
    } catch (err) {
      setError('Error al guardar la configuración');
      console.error('Error saving config:', err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field: keyof CompanyConfig, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex align-items-center">
              <Settings size={24} className="me-2" />
              <h4 className="mb-0">Configuración de la Empresa</h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre de la Empresa *</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={config.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        placeholder="Ingrese el nombre de la empresa"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email de la Empresa *</Form.Label>
                      <Form.Control
                        type="email"
                        required
                        value={config.companyEmail}
                        onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                        placeholder="empresa@ejemplo.com"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Teléfono</Form.Label>
                      <Form.Control
                        type="text"
                        value={config.companyPhone}
                        onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                        placeholder="+34 xxx xxx xxx"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>NIF/CIF *</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={config.companyTaxId}
                        onChange={(e) => handleInputChange('companyTaxId', e.target.value)}
                        placeholder="B12345678"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={config.companyAddress}
                    onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                    placeholder="Dirección completa de la empresa"
                  />
                </Form.Group>
                
                <hr />
                
                <Form.Group className="mb-3">
                  <Form.Label>Logo de la Empresa</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <Form.Text className="text-muted">
                    Suba una imagen para el logo de la empresa (aparecerá en PDFs)
                  </Form.Text>
                  {previewUrl && (
                    <div className="mt-2">
                      <Image 
                        src={previewUrl} 
                        alt="Logo preview" 
                        style={{ maxWidth: '200px', maxHeight: '100px' }}
                        thumbnail
                      />
                    </div>
                  )}
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Pie de Página para PDFs</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={config.invoiceFooter}
                    onChange={(e) => handleInputChange('invoiceFooter', e.target.value)}
                    placeholder="Texto que aparecerá en el pie de página de facturas y presupuestos"
                  />
                  <Form.Text className="text-muted">
                    Ejemplo: "Gracias por su confianza. Condiciones de pago: 30 días."
                  </Form.Text>
                </Form.Group>
                
                <div className="d-flex justify-content-end">
                  <Button variant="primary" type="submit">
                    <Save size={20} className="me-2" />
                    Guardar Configuración
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Configuracion;